import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import { useBatchProductSearch } from '../../../hooks/useBatchProductSearch';

export interface BatchFilterConditions {
  receivedAt?: DateRange;
  expiryDate?: DateRange;
  productIds?: string[];
}

interface BatchFiltersPanelProps {
  conditions: BatchFilterConditions;
  onChange: (conditions: BatchFilterConditions) => void;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export const BatchFiltersPanel = ({ conditions, onChange }: BatchFiltersPanelProps) => {
  const { t } = useTranslation('batchesModule');
  const { loadProductsForMultiSelect } = useBatchProductSearch();

  const productIds = conditions.productIds ?? [];

  const activeCount = [conditions.receivedAt?.from, conditions.expiryDate?.from, productIds.length > 0].filter(
    Boolean,
  ).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('table.product')}>
          <MultiSelect
            loadOptions={loadProductsForMultiSelect}
            value={productIds}
            onValueChange={(values) => onChange({ ...conditions, productIds: values })}
            placeholder={t('filters.all_products')}
            emptyText={t('filters.product_not_found')}
            noResultsText={t('filters.product_not_found')}
            searchPlaceholder={t('filters.product_placeholder')}
            dataTestId="filter-product"
            triggerClassName="h-[38px]"
            pageSize={10}
          />
        </FilterField>

        <FilterField label={t('filters.received_at')}>
          <DateRangePicker
            initialDateFrom={conditions.receivedAt?.from}
            initialDateTo={conditions.receivedAt?.to}
            onUpdate={({ range }) => onChange({ ...conditions, receivedAt: range })}
            placeholder={t('filters.date_placeholder')}
            triggerClassName="h-[38px] text-sm"
            dataTestId="filter-received-at"
          />
        </FilterField>

        <FilterField label={t('filters.expiry_date')}>
          <DateRangePicker
            initialDateFrom={conditions.expiryDate?.from}
            initialDateTo={conditions.expiryDate?.to}
            onUpdate={({ range }) => onChange({ ...conditions, expiryDate: range })}
            placeholder={t('filters.date_placeholder')}
            triggerClassName="h-[38px] text-sm"
            dataTestId="filter-expiry-date"
          />
        </FilterField>
      </div>

      {activeCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={() => onChange({})}
            type="button"
          >
            {t('filters.clear', { count: activeCount })}
          </Button>
        </div>
      )}
    </div>
  );
};
